// PracticeManagementSoftware.jsx
import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  Box,
  Button,
  Flex,
  Select,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { WarningIcon } from "@chakra-ui/icons";
import { KollaSDKProvider } from "@kolla/react-sdk";
import { cqAPI, Auth } from "../../lib/Auth";
import ClientTokenSettings from "./ClientTokenSettings";

/* --------------------------------------------------------------
   SETTINGS
   -------------------------------------------------------------- */

// Adjust this selector so it matches your actual Kolla button
const KOLLA_BTN_SELECTOR = ".kolla-connector-button";

/* --------------------------------------------------------------
   OVERLAY LAYER
   - Transparent overlay that sits over a target element, intercepts the click,
     opens the modal, and only proceeds if the user confirms.
   -------------------------------------------------------------- */

const Overlay = ({ target, onIntercept }) => {
  const [rect, setRect] = useState(null);

  const updateRect = useCallback(() => {
    if (!target) return;
    const r = target.getBoundingClientRect();
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, [target]);

  useEffect(() => {
    if (!target) return;
    updateRect();

    const handle = () => updateRect();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle, true);

    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(updateRect);
      try {
        ro.observe(target);
      } catch {}
    }

    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle, true);
      if (ro) ro.disconnect();
    };
  }, [target, updateRect]);

  if (!rect) return null;

  return ReactDOM.createPortal(
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onIntercept(target);
      }}
      style={{
        position: "fixed",
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        zIndex: 9999,
        background: "transparent",
        cursor: "pointer",
      }}
    />,
    document.body
  );
};

/* --------------------------------------------------------------
   OVERLAY MANAGER
   -------------------------------------------------------------- */

const KollaClickGate = ({ selector, ensureToken }) => {
  const [targets, setTargets] = useState([]);
  const [allowed, setAllowed] = useState(new Set()); // switched from WeakSet
  const [pendingTarget, setPendingTarget] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    const findTargets = () => {
      const found = Array.from(document.querySelectorAll(selector)).filter(Boolean);
      setTargets(found);
    };

    findTargets();

    const mo = new MutationObserver(() => findTargets());
    mo.observe(document.documentElement, { childList: true, subtree: true });

    const t = setTimeout(findTargets, 0);

    return () => {
      mo.disconnect();
      clearTimeout(t);
    };
  }, [selector]);

  const intercept = useCallback(
    (tgt) => {
      if (allowed.has(tgt)) return;
      setPendingTarget(tgt);
      onOpen();
    },
    [allowed, onOpen]
  );

  const handleNo = () => {
    onClose();
    setPendingTarget(null);
    toast({
      title: "Please use the server",
      description:
        "Please switch to the server that hosts your practice-management software and try again from there.",
      status: "info",
      duration: 7000,
      isClosable: true,
    });
  };

  const handleYes = async () => {
    const tgt = pendingTarget;
    setPendingTarget(null);
    onClose();

    try {
      if (ensureToken) {
        await ensureToken();
      }

      if (tgt) {
        setAllowed((prev) => {
          const next = new Set(prev);
          next.add(tgt);
          return next;
        });

        Promise.resolve().then(() => {
          if (typeof tgt.click === "function") {
            tgt.click();
          } else if (window.Kolla && typeof window.Kolla.open === "function") {
            window.Kolla.open();
          } else {
            throw new Error("Could not find a way to launch Kolla.");
          }
        });
      }
    } catch (err) {
      toast({
        title: "Couldn’t start Kolla",
        description: err?.message || "Please try again.",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      {targets.map((tgt, i) =>
        allowed.has(tgt) ? null : (
          <Overlay key={i} target={tgt} onIntercept={intercept} />
        )
      )}

      {/* Important Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={handleNo} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            bg="red.500"
            color="white"
            display="flex"
            alignItems="center"
            gap={2}
          >
            <WarningIcon />
            Important Confirmation
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <Text mb={2} fontWeight="bold" color="red.600">
              For best results, run this from the <b>server</b> that hosts your
              practice-management software (not a workstation).
            </Text>
            <Text>Are you currently on that server?</Text>
          </ModalBody>
<ModalFooter as={Flex} justify="flex-end" gap={4}>
  <Button variant="outline" colorScheme="red" onClick={handleNo}>
    No, I’m on a workstation
  </Button>
  <Button colorScheme="red" onClick={handleYes}>
    Yes, I’m on the server
  </Button>
</ModalFooter>
          
        </ModalContent>
      </Modal>
    </>
  );
};

/* --------------------------------------------------------------
   MAIN COMPONENT
   -------------------------------------------------------------- */

const PracticeManagementSoftware = () => {
  const [pmsOptions, setPmsOptions] = useState({});
  const [selectedPMS, setSelectedPMS] = useState("");
  const [workerType, setWorkerType] = useState("");
  const [pmsType, setPMSType] = useState("");
  const [supportedPMSVersions, setSupportedPMSVersions] = useState([]);
  const [pmsVersion, setPmsVersion] = useState("");
  const [configs, setConfigs] = useState([]);
  const [business, setBusiness] = useState({});
  const [supportedPMS, setSupportedPMS] = useState({});
  const [unusedPMS, setUnusedPMS] = useState({});
  const [lastDataReceived, setLastDataReceived] = useState({});
  const [kollaConsumertoken, setKollaConsumerToken] = useState("");
  const [pmsStatus, setPmsStatus] = useState({});

  const fetchClientConfig = async () => {
    const config = await cqAPI.get("/settings/client");
    setLastDataReceived(config?.data?.lastDataReceived || {});
    const cfgs = config?.data?.configs || [];
    const sp = config?.data?.SUPPORTED_PMS || {};
    const pmsStatus = config?.data?.pmsStatus || {};
    setPmsOptions(config?.data);
    setConfigs(cfgs);
    setBusiness(config?.data?.business || {});
    setSupportedPMS(sp);
    setPmsStatus(pmsStatus);

    const selectablePMS = Object.fromEntries(
      Object.entries(sp).filter(
        ([, entry]) =>
          !cfgs.find(
            (c) => entry.worker_type === c.worker_type && entry.pms_type === c.pms_type
          )
      )
    );
    setUnusedPMS(selectablePMS);
  };

  useEffect(() => {
    fetchClientConfig();
  }, []);

  const postMultipleClientFields = async (value) => {
    await cqAPI.post("/settings/client", value);
    await fetchClientConfig();
    return true;
  };

  const selectPMS = (pmsName) => {
    const pms = pmsOptions?.SUPPORTED_PMS?.[pmsName];
    if (!pms) {
      setSelectedPMS("");
      setWorkerType("");
      setPMSType("");
      setSupportedPMSVersions([]);
      return;
    }
    setSelectedPMS(pmsName);
    setSupportedPMSVersions(pms.versions || []);
    setWorkerType(pms.worker_type);
    setPMSType(pms.pms_type);
    if (pmsName !== selectedPMS) {
      setPmsVersion((pms.versions && pms.versions[0]) || "");
    }
  };

  const addNewClient = async () => {
    if (!workerType || !pmsType) return;
    if (supportedPMSVersions.length && !pmsVersion) return;
    await postMultipleClientFields({
      worker_type: workerType,
      pms_type: pmsType,
      pms_version: pmsVersion,
    });
  };

  const requestKollaConsumerToken = async () => {
    if (kollaConsumertoken) return;
    const response = await cqAPI.post("/settings/client/consumerToken");
    if (response?.type === "ok") {
      const { data: { token } = {} } = response;
      if (token) setKollaConsumerToken(token);
    }
  };

  return (
    <Box p={4} overflowY="auto" w="100%">
      <VStack spacing={4} align="stretch">
        <Flex alignItems="center" justifyContent="flex-start" gap={12} wrap="wrap">
          {(configs.length === 0 || Auth.user?.isSuperAdmin) && (<Select
            placeholder="Select PMS"
            onChange={(e) => selectPMS(e.target.value)}
            value={selectedPMS}
            width="initial"
          >
            {unusedPMS &&
              Object.entries(unusedPMS).map(([key]) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
          </Select>)}

          {!!supportedPMSVersions.length && (
            <Select
              value={pmsVersion}
              width="initial"
              onChange={(e) => setPmsVersion(e.target.value)}
            >
              {supportedPMSVersions.map((version, index) => (
                <option key={index} value={version}>
                  {version}
                </option>
              ))}
            </Select>
          )}

          {(configs.length === 0 || Auth.user?.isSuperAdmin) && (<Button colorScheme="blue" onClick={addNewClient}>
            Add new PMS adapter
          </Button>)}
        </Flex>

        <KollaSDKProvider token={kollaConsumertoken || undefined}>
          {configs.map((config) => (
            <ClientTokenSettings
              key={config?._id}
              lastDataReceived={lastDataReceived}
              business={business}
              config={config}
              supportedPMS={supportedPMS}
              fetchClientConfig={fetchClientConfig}
              pmsSyncJob={pmsOptions?.pmsSyncJob}
              kollaConsumertoken={kollaConsumertoken}
              setKollaConsumerToken={setKollaConsumerToken}
              requestKollaConsumerToken={requestKollaConsumerToken}
              pmsStatus={pmsStatus}
            />
          ))}

          {/* Overlay gate for Kolla button */}
          <KollaClickGate
            selector={KOLLA_BTN_SELECTOR}
            ensureToken={requestKollaConsumerToken}
          />
        </KollaSDKProvider>
      </VStack>
    </Box>
  );
};

export default PracticeManagementSoftware;
